import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Single-insight callout — a small floating card with a leading emoji
 * acting as the visual anchor and a short body. Use for:
 *   "💡 תובנה: …"
 *   "🎯 מטרה: …"
 *   "⚡ פעולה: …"
 *
 * Routes from a paragraph that starts with an emoji on its own and is
 * <= ~280 chars (parser-level decision).
 */
export type InsightTone =
  | "amber"
  | "blue"
  | "violet"
  | "emerald"
  | "rose"
  | "stone";

const TONES: Record<
  InsightTone,
  { bg: string; ring: string; emoji: string; text: string }
> = {
  amber: {
    bg: "bg-amber-100",
    ring: "ring-amber-200/60",
    emoji: "bg-amber-200",
    text: "text-stone-900",
  },
  blue: {
    bg: "bg-sky-100",
    ring: "ring-sky-200/60",
    emoji: "bg-sky-200",
    text: "text-stone-900",
  },
  violet: {
    bg: "bg-violet-100",
    ring: "ring-violet-200/55",
    emoji: "bg-violet-200",
    text: "text-stone-900",
  },
  emerald: {
    bg: "bg-emerald-100",
    ring: "ring-emerald-200/55",
    emoji: "bg-emerald-200",
    text: "text-stone-900",
  },
  rose: {
    bg: "bg-rose-100",
    ring: "ring-rose-200/55",
    emoji: "bg-rose-200",
    text: "text-stone-900",
  },
  stone: {
    bg: "bg-stone-200",
    ring: "ring-stone-300/60",
    emoji: "bg-white",
    text: "text-stone-900",
  },
};

export type InsightCardProps = {
  emoji: string;
  tone?: InsightTone;
  children: ReactNode;
  /** Optional small uppercase label, e.g. "תובנה" / "פעולה". */
  label?: string;
  className?: string;
};

export function InsightCard({
  emoji,
  tone = "amber",
  children,
  label,
  className,
}: InsightCardProps) {
  const t = TONES[tone];
  return (
    <aside
      className={cn(
        "rounded-2xl px-5 py-4 sm:px-6 sm:py-5 my-5",
        "flex items-start gap-4",
        t.bg,
        t.text,
        className,
      )}
    >
      <span
        className={cn(
          "shrink-0 flex items-center justify-center",
          "h-11 w-11 rounded-xl text-xl",
          t.emoji,
        )}
        aria-hidden
      >
        {emoji}
      </span>
      <div className="min-w-0 flex-1 leading-relaxed">
        {label ? (
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] opacity-65 mb-1.5">
            {label}
          </p>
        ) : null}
        <div className="text-[15px] leading-[1.7]">{children}</div>
      </div>
    </aside>
  );
}
